import React, { useState } from 'react';
import styled from '@emotion/styled';
import { CheckSquare, Plus, Clock, User, AlertCircle, Calendar, Filter } from 'lucide-react';

const PageContainer = styled.div`
  background: white;
  border-radius: 8px;
  margin: 1rem;
  min-height: 400px;
  padding-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 1rem;
  padding: 1.5rem 1.5rem 0 1.5rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  font-size: 1rem;
  margin-bottom: 2rem;
  padding: 0 1.5rem;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem 1.5rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.active ? '#f3f4f6' : 'white'};
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: ${props => props.active ? '#374151' : '#6b7280'};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f9fafb;
    border-color: #9ca3af;
  }
`;

const AddTaskButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: #7764f7;
  color: white;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: #6d4ce6;
  }
`;

const TasksGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  padding: 0 1.5rem;
`;

const TaskCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  transition: all 0.2s;
  
  &:hover {
    border-color: #d1d5db;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }
`;

const TaskHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
`;

const TaskTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
`;

const TaskStatus = styled.span`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${props => {
    switch(props.status) {
      case 'pending': return '#fef3c7';
      case 'in-progress': return '#dbeafe';
      case 'completed': return '#d1fae5';
      default: return '#f3f4f6';
    }
  }};
  color: ${props => {
    switch(props.status) {
      case 'pending': return '#92400e';
      case 'in-progress': return '#1e40af';
      case 'completed': return '#065f46';
      default: return '#374151';
    }
  }};
`;

const TaskDescription = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  line-height: 1.5;
`;

const TaskMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: #9ca3af;
`;

const TaskAssignee = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TaskDueDate = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
`;

const EmptyStateIcon = styled.div`
  width: 64px;
  height: 64px;
  background: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
  color: #9ca3af;
`;

const Tasks = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  
  // FPO task data
  const tasks = [
    {
      id: 1,
      title: 'Review Loan #1048084238',
      description: 'Validate borrower information and verify document accuracy for high-priority case.',
      status: 'in-progress',
      assignee: 'Gary',
      dueDate: '2024-01-15',
      priority: 'high'
    },
    {
      id: 2,
      title: 'Update Field Mappings',
      description: 'Configure new field mappings for Portfolio Scrub Reports based on client requirements.',
      status: 'pending',
      assignee: 'Sarah',
      dueDate: '2024-01-18',
      priority: 'medium'
    },
    {
      id: 3,
      title: 'Quality Check Completed Cases',
      description: 'Review and validate all cases marked as completed in the last week.',
      status: 'completed',
      assignee: 'Mike',
      dueDate: '2024-01-12',
      priority: 'low'
    },
    {
      id: 4,
      title: 'System Performance Review',
      description: 'Analyze system performance metrics and identify optimization opportunities.',
      status: 'pending',
      assignee: 'Gary',
      dueDate: '2024-01-20',
      priority: 'medium'
    }
  ];

  const filteredTasks = activeFilter === 'all' 
    ? tasks 
    : tasks.filter(task => task.status === activeFilter);

  const getStatusLabel = (status) => {
    switch(status) {
      case 'pending': return 'Pending';
      case 'in-progress': return 'In Progress';
      case 'completed': return 'Completed';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <PageContainer>
      <Title>Task Management</Title>
      <Subtitle>Track and manage workflow tasks across the system</Subtitle>
      
      <HeaderSection>
        <FilterSection>
          <FilterButton 
            active={activeFilter === 'all'} 
            onClick={() => setActiveFilter('all')}
          >
            <Filter size={16} />
            All Tasks
          </FilterButton>
          <FilterButton 
            active={activeFilter === 'pending'} 
            onClick={() => setActiveFilter('pending')}
          >
            <Clock size={16} />
            Pending
          </FilterButton>
          <FilterButton 
            active={activeFilter === 'in-progress'} 
            onClick={() => setActiveFilter('in-progress')}
          >
            <AlertCircle size={16} />
            In Progress
          </FilterButton>
          <FilterButton 
            active={activeFilter === 'completed'} 
            onClick={() => setActiveFilter('completed')}
          >
            <CheckSquare size={16} />
            Completed
          </FilterButton>
        </FilterSection>
        
        <AddTaskButton>
          <Plus size={16} />
          Add Task
        </AddTaskButton>
      </HeaderSection>

      {filteredTasks.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>
            <CheckSquare size={24} />
          </EmptyStateIcon>
          <h3>No tasks found</h3>
          <p>No tasks match the current filter. Try adjusting your filter or add a new task.</p>
        </EmptyState>
      ) : (
        <TasksGrid>
          {filteredTasks.map(task => (
            <TaskCard key={task.id}>
              <TaskHeader>
                <TaskTitle>{task.title}</TaskTitle>
                <TaskStatus status={task.status}>
                  {getStatusLabel(task.status)}
                </TaskStatus>
              </TaskHeader>
              
              <TaskDescription>{task.description}</TaskDescription>
              
              <TaskMeta>
                <TaskAssignee>
                  <User size={14} />
                  {task.assignee}
                </TaskAssignee>
                <TaskDueDate>
                  <Calendar size={14} />
                  Due {formatDate(task.dueDate)}
                </TaskDueDate>
              </TaskMeta>
            </TaskCard>
          ))}
        </TasksGrid>
      )}
    </PageContainer>
  );
};

export default Tasks;
